import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilState, useSetRecoilState } from 'recoil';

import { deleteMyscribeAIPrompt, getGeneralChatThreadMessages } from 'api/MyScribeAi';

import { stopResponseGeneration } from 'components/practice/charts/ClinicalNote/MyScribeAI/Templates/MyScribeTemplate/lib/helpers';
import { showAlert } from 'components/shared/Alert/Alert';
import Icon from 'components/shared/Icon/Icon';
import Popover from 'components/shared/Popovers/Popover/Popover';

import {
  generalChatActivePromptConversationsState,
  generalChatActivePromptIdState,
  generalChatGenerating,
  generalChatIdState,
  generalChatLoading,
  generalChatPromptListState
} from './state';

export const Sidebar = () => {
  const [hoverPromptId, setHoverPromptId] = useState(null);
  const [isLoading, setIsLoading] = useRecoilState(generalChatLoading);
  const [isGenerating, setIsGenerating] = useRecoilState(generalChatGenerating);
  const [intelligenceChatId, setIntelligenceChatId] = useRecoilState(generalChatIdState);
  const [activePromptId, setActivePromptId] = useRecoilState(generalChatActivePromptIdState);
  const setActivePromptConversations = useSetRecoilState(generalChatActivePromptConversationsState);
  const [generalChatPromptList, setGeneralChatPromptList] = useRecoilState(
    generalChatPromptListState
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const onThreadClick = async (prompt) => {
    const { id } = prompt;
    try {
      if ((!!isGenerating || isLoading) && id !== intelligenceChatId) {
        setIsGenerating(false);
        setIsLoading(false);
        stopResponseGeneration({ intelligenceChatId });
      }
      const { chat } = await getGeneralChatThreadMessages(navigate, { id });
      setActivePromptId(id);
      const newConversations = chat.map((item) =>
        item?.agent === 'ai' ? { ...item, message: item.message } : item
      );
      setIntelligenceChatId(chat[0]?.intelligence_chat_id);
      setActivePromptConversations(newConversations);
    } catch (error) {
      console.error({ error });
    }
  };

  const addNewPrompt = () => {
    if (!!isGenerating) {
      setIsLoading(false);
      setIsGenerating(false);
      stopResponseGeneration({ intelligenceChatId });
    }
    setActivePromptId(null);
    setActivePromptConversations(null);
    setIntelligenceChatId(null);
  };

  const onDeletePrompt = async (id) => {
    try {
      const { status, message } = await deleteMyscribeAIPrompt(navigate, { id });
      if (status === 'deleted') {
        showAlert({ title: 'Prompt Deleted', message, color: 'success' });
        queryClient.setQueryData(['general-chat-threads'], ({ threads = [], ...rest }) => {
          return { threads: threads.filter((item) => item.id !== id), ...rest };
        });
        setGeneralChatPromptList((prev) => prev.filter((prompt) => prompt.id !== id));
        setActivePromptConversations(null);
        setActivePromptId(null);
        setIntelligenceChatId(null);
      }
    } catch (error) {
      console.error('Error deleting prompt: ', error);
    }
  };

  const getPopoverActions = (id) => [
    { label: 'Delete', icon: 'trash-mono', onClick: () => onDeletePrompt(id) }
  ];

  const onMouseEnter = (id) => setHoverPromptId(id);
  const onMouseLeave = () => setHoverPromptId(null);

  return (
    <div className="w-[250px] min-w-[250px] border-r bg-white px-2.5 py-4 shadow-lg">
      <div className="flex h-[40px] items-center justify-between bg-white px-4">
        <h2 className="text-lg font-semibold">Chat History</h2>
        <Icon icon="edit-4-linear" size={20} color="primary" onClick={addNewPrompt} />
      </div>
      <div className="h-[calc(100%-50px)] overflow-y-auto">
        {generalChatPromptList?.map((prompt) => (
          <Tippy
            disabled={!prompt?.name}
            key={prompt.id}
            content={prompt?.name}
            placement="right"
            className="tippy-dark">
            <div
              onClick={() => onThreadClick(prompt)}
              onMouseEnter={() => onMouseEnter(prompt.id)}
              onMouseLeave={() => onMouseLeave(prompt.id)}
              className={cs(
                'my-3 mb-4 max-h-[108px] w-full cursor-pointer rounded-lg p-2 hover:bg-primary-50',
                activePromptId === prompt.id ? 'bg-primary-50' : 'bg-white'
              )}>
              <div className="relative flex h-[26px] justify-between">
                <div className="mb-2 flex items-center">
                  <div className="flex gap-[6px]">
                    <Icon icon="message-mono" />
                    <span
                      className={cs(
                        'w-full truncate text-sm font-semibold text-neutral-800',
                        hoverPromptId === prompt?.id ? 'max-w-[175px]' : 'max-w-[190px]'
                      )}>
                      {prompt?.name?.replace(/"/g, '')}
                    </span>
                  </div>
                </div>
                {hoverPromptId === prompt?.id && (
                  <Popover
                    className="absolute -right-1 -top-1 z-50"
                    icon="context-menu-horizontal"
                    isBorder={false}
                    isFixed
                    options={getPopoverActions(prompt.id)}
                  />
                )}
              </div>
              <p className="line-clamp-2 text-xs font-thin leading-[20px] text-neutral-700">
                {prompt.description}
              </p>
            </div>
          </Tippy>
        ))}
      </div>
    </div>
  );
};
